// apps/web/src/components/threat-actors/ThreatActorDossierRow.tsx
//
// KAN-221 — one dense dossier row in the ranked actor feed. The whole row is a
// single <Link> to /threat-actors/:id; everything inside is non-interactive
// (spans / an icon), satisfying the "no nested interactive controls inside a
// Link" accessibility requirement. All fields come from the real ThreatActor
// shape via the pure helpers in threat-actor-utils.

import { Link } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import type { ThreatActor } from '@/lib/types'
import { campaignsSurfaceEnabled } from '@/lib/campaigns-surface'
import { TypeStatusInline } from '@/components/campaigns/TypeStatus'
import {
  attributionBadgeClasses,
  attributionLabel,
  referenceCount,
} from './threat-actor-utils'

export interface ThreatActorDossierRowProps {
  actor: ThreatActor
  rank: number
  now?: Date
}

export function ThreatActorDossierRow({ actor, rank }: ThreatActorDossierRowProps) {
  const aliases = actor.aliases?.filter(Boolean) ?? []
  const aliasLine = aliases.length
    ? `aka ${aliases.slice(0, 2).join(', ')}`
    : 'no known aliases'
  const aliasMore = aliases.length > 2 ? `+${aliases.length - 2}` : ''

  const refs = referenceCount(actor)

  return (
    <Link
      data-testid="threat-actor-card"
      to={`/threat-actors/${actor.id}`}
      className="group block border-b border-border/60 px-5 py-4 transition-colors last:border-0 hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
    >
      {/* top line */}
      <div className="flex items-start gap-3.5">
        <span className="min-w-[22px] shrink-0 pt-0.5 font-mono text-xs text-muted-foreground/70">
          {String(rank).padStart(2, '0')}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-2.5 gap-y-1">
            <span className="font-heading text-[15px] font-semibold tracking-tight text-foreground">
              {actor.name}
            </span>
            <span className="truncate font-mono text-xs text-muted-foreground">
              {aliasLine}
            </span>
            {aliasMore && (
              <span className="text-[11px] text-muted-foreground/70">{aliasMore}</span>
            )}
            {/* KAN-736 R25 (KAN-830 PR 10): plain neutral text, flag-gated. */}
            {campaignsSurfaceEnabled() && <TypeStatusInline typeStatus={actor.typeStatus} />}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <span
            className={[
              'inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium',
              attributionBadgeClasses(actor.attribution),
            ].join(' ')}
          >
            {attributionLabel(actor.attribution)}
          </span>

        </div>

        <ChevronRight
          className="mt-0.5 h-4 w-4 shrink-0 self-center text-muted-foreground/50 transition-colors group-hover:text-foreground"
          aria-hidden="true"
        />
      </div>

      {/* description excerpt */}
      {actor.description && (
        <p className="mt-1.5 line-clamp-1 max-w-3xl pl-[34px] text-[13px] text-muted-foreground">
          {actor.description}
        </p>
      )}

      {/* meta line */}
      <div className="mt-2.5 flex flex-wrap items-center gap-x-5 gap-y-2 pl-[34px]">

        <span className="ml-auto flex items-center gap-1.5">
          {actor.mitreId && (
            <span
              className="rounded border border-severity-high/40 px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-severity-high"
              title={`MITRE ATT&CK group ${actor.mitreId}`}
            >
              {actor.mitreId}
            </span>
          )}
          {actor.mispUuid && (
            <span
              className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-muted-foreground"
              title="MISP galaxy entry available"
            >
              MISP
            </span>
          )}
          {actor.malpediaId && (
            <span
              className="rounded border border-border px-1.5 py-0.5 font-mono text-[10px] font-semibold tracking-wide text-muted-foreground"
              title="Malpedia profile available"
            >
              MALPEDIA
            </span>
          )}
          {refs > 0 && (
            <span
              className="rounded border border-border/60 px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground/70"
              title={`${refs} external reference${refs === 1 ? '' : 's'} on file`}
            >
              {refs} refs
            </span>
          )}
        </span>
      </div>
    </Link>
  )
}
